import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Bar as BarChart } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { externalTooltipHandler } from 'components/Chart/Chart/externalTooltipHandler'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)
let delayed
const ANIMATION_DURATION = 1 //sec

const options = {
  responsive: true,
  maintainAspectRatio: false,

  plugins: {
    verticalLinePlugin: {
      lineColor: 'transparent',
    },
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: externalTooltipHandler,
    },
  },

  interaction: {
    mode: 'index',
    intersect: false,
  },

  scales: {
    x: {
      beginAtZero: true,

      grid: {
        drawBorder: false,
        display: false,
      },
      ticks: {
        color: '#1E2F4B',
        font: {
          size: 11,
        },
      },
      clip: { bottom: 100 },
    },
    y0: {
      display: false,
      grid: {
        display: false,
      },
      position: 'left',
      type: 'linear',
    },
    y1: {
      display: false,
      grid: {
        display: false,
      },
      position: 'right',
      type: 'linear',
    },
    y2: {
      display: false,
      grid: {
        display: false,
      },
      position: 'left',
      type: 'linear',
    },
    y3: {
      display: false,
      grid: {
        display: false,
      },
      position: 'right',
      type: 'linear',
    },
  },
}

export const Bar = ({
  items = [],
  metrics = [],
  labels = [],
  include_vat,
  measure = {},
  isMobile = false,
  colors,
}) => {
  const { t } = useTranslation(['pulse'])
  const labelsLength = labels.length
  const isSmallWindow = window.innerWidth < 768
  const barThickness = labelsLength > 15 || isSmallWindow ? 6 : 15
  const maxBarThickness = labelsLength > 15 || isSmallWindow ? 4 : 9

  const mobileEvents = useMemo(
    () => (isMobile ? { events: ['click', 'mouseenter'] } : {}),
    [isMobile],
  )

  const data = {
    labels,
    datasets: metrics.map(({ id, isHidden }, index) => {
      const hasChildren = id.indexOf('.') > -1
      let currentLabel
      if (hasChildren) {
        const [key, value] = id.split('.')
        currentLabel = t(`chart.legend.labels.${key}.${value}`)
      } else {
        currentLabel = t(`chart.legend.labels.${id}`)
      }

      return {
        labelSuffix: measure[id] || '',
        label: currentLabel,
        yAxisID: 'y' + index,
        data: items.map((item) => {
          let currentItem
          if (hasChildren) {
            const [key, value] = id.split('.')
            currentItem = item?.[key]?.[value] ?? { value: 0 }
          } else {
            currentItem = item?.[id] ?? { value: 0 }
          }
          return include_vat && currentItem.valueIncludeVat
            ? currentItem.valueIncludeVat
            : currentItem.value
        }),
        backgroundColor: colors[index],
        barThickness: barThickness,
        maxBarThickness: maxBarThickness,
        hidden: !!isHidden,
        borderRadius: 5,
      }
    }),
  }

  const animation = {
    onComplete: () => {
      delayed = true
    },
    delay: (context) => {
      let delay = 0
      if (context.type === 'data' && context.mode === 'default' && !delayed) {
        delay =
          context.dataIndex *
            ((ANIMATION_DURATION * 1000) / (labels.length || 1)) +
          context.datasetIndex * 100
      }
      return delay
    },
  }

  options.animation = animation

  return (
    <BarChart
      data-is-mobile={isMobile}
      type="bar"
      options={{ ...options, ...mobileEvents }}
      data={data}
    />
  )
}

Bar.defaultProps = {
  items: [],
  metrics: [],
  labels: [],
  include_vat: false,
  measure: {},
  isMobile: false,
  colors: [],
}

Bar.propTypes = {
  items: PropTypes.array,
  metrics: PropTypes.array,
  labels: PropTypes.array,
  include_vat: PropTypes.bool,
  measure: PropTypes.object,
  isMobile: PropTypes.bool,
  colors: PropTypes.array,
}
